import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from 'react-i18next';
import axios from "axios"
import { useSelector, useDispatch } from 'react-redux';

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Modal from 'react-bootstrap/Modal';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import ToggleButton from 'react-bootstrap/ToggleButton';
import { Link, NavLink } from "react-router-dom";
import AgendaItemEditor from "./AgendaItemEditor";
import { renumberTags, buildGroups, startNumberOf, resequencePositions } from "./agendaNumbering";

import {
    DndContext,
    closestCenter,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    verticalListSortingStrategy,
    sortableKeyboardCoordinates,
    arrayMove,
} from "@dnd-kit/sortable";

import {
    AuthenticatedTemplate,
    UnauthenticatedTemplate,
    useMsal,
    useIsAuthenticated,
    useAccount
} from "@azure/msal-react"
import authConfig from "../../authConfig";
import MeetingAgendaItemClonePopup from "./MeetingAgendaItemClonePopup";
function AgendaEditor(props) {
    const { t, i18n } = useTranslation();
    const [language, setLanguage] = useState("en"); //useSelector((state) => state.language.value);
    const [show, setShow] = useState(false);

    const isAuthenticated = useIsAuthenticated();
    const { instance, accounts } = useMsal()
    const account = useAccount(accounts[0] || {})

    // Auto-numbering toggle. Default OFF: reordering/adding preserves the new
    // order (positionIndex) but does not rewrite item tags. When ON, tags are
    // auto-sequenced on reorder, insert, add and tag edits. Persisted in the
    // database per-user (via the userpreference API), which is the single source
    // of truth. Defaults to OFF until the stored preference has loaded.
    const AUTO_NUMBER_PREF = "agendaAutoNumber";
    const [autoNumber, setAutoNumber] = useState(false);
    // Guards against writing the preference back during the initial load.
    const [prefLoaded, setPrefLoaded] = useState(false);
    // Ensures the stored preference is loaded from the database exactly once.
    // Without this, the load effect could re-run when getApiTokenAsync changes
    // (e.g. on a silent MSAL token/account refresh) and revert an in-session
    // toggle change back to the stale stored value.
    const prefLoadStartedRef = useRef(false);

    // Acquire an access token for the API, matching getNewItemAsync's pattern.
    const getApiTokenAsync = useCallback(async () => {
        if (!isAuthenticated || !account) return null;
        try {
            const response = await instance.acquireTokenSilent({
                scopes: authConfig.api.scopes,
                account: account
            });
            return response ? response.accessToken : null;
        } catch (error) {
            console.error(error);
            return null;
        }
    }, [isAuthenticated, account, instance]);

    // Load the persisted preference from the database once on mount.
    useEffect(() => {
        if (prefLoadStartedRef.current) return; // load exactly once
        let cancelled = false;
        (async () => {
            const token = await getApiTokenAsync();
            // Not authenticated yet: leave the guard unset so a later run (once a
            // token is available) can still perform the initial load.
            if (!token) return;
            prefLoadStartedRef.current = true; // committed: never load again
            try {
                const resp = await axios
                    .create({ headers: { Authorization: `Bearer ${token}` } })
                    .get(`${authConfig.api.urls.getUserPreference}/${AUTO_NUMBER_PREF}`);
                if (!cancelled && resp.data && resp.data.value != null) {
                    setAutoNumber(resp.data.value === "true");
                }
            } catch (error) {
                console.error(error);
            }
            if (!cancelled) setPrefLoaded(true);
        })();
        return () => { cancelled = true; };
    }, [getApiTokenAsync]);

    // Persist the preference to the database (once the initial load has completed).
    useEffect(() => {
        if (!prefLoaded) return;
        (async () => {
            const token = await getApiTokenAsync();
            if (!token) return;
            try {
                await axios
                    .create({ headers: { Authorization: `Bearer ${token}` } })
                    .put(`${authConfig.api.urls.getUserPreference}/${AUTO_NUMBER_PREF}`, {
                        value: autoNumber ? "true" : "false"
                    });
            } catch (error) {
                console.error(error);
            }
        })();
    }, [autoNumber, prefLoaded, getApiTokenAsync]);

    // Apply tag renumbering when auto-numbering is on; otherwise only resequence
    // positionIndex so the order still persists without touching the tags.
    const applyNumbering = (items, startOverride, force) =>
        autoNumber ? renumberTags(items, startOverride, force) : resequencePositions(items);

    async function getNewItemAsync() {
        let returnvalue = {};
        let authresponse = null;


        if (isAuthenticated) {
            if (account /*&& loading == false*/) {
                //setLoading(true)
                await instance
                    .acquireTokenSilent({
                        scopes: authConfig.api.scopes,
                        account: account
                    })
                    .then(response => {
                        authresponse = response;
                    })
                    .catch(error => {
                        //setError("Error getting token: " + error.message)
                        console.error(error)
                        //setLoading(false)
                    });
                if (authresponse) {
                    //console.log("Got access token:", authresponse);
                    await axios
                        .create({
                            headers: { Authorization: `Bearer ${authresponse.accessToken}` }
                        })
                        .get(authConfig.api.urls.getAgendaItemNew)
                        .then(function (response) {
                            //console.log(response.data);
                            returnvalue = response.data;
                            return response.data;
                        })
                        .catch(function (error) {
                            console.error(error)
                        })
                        .then(function () {
                            // always executed
                            //setLoading(false)
                        })
                } else {
                    //setError("Empty token??")
                    console.error("no token acquired")
                }
            }
        }
        return returnvalue;
    }


    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }
        const data = [...props.data];
        const groups = buildGroups(data);

        const activeGroupIdx = groups.findIndex((g) => g.some((x) => x.id === active.id));
        const overGroupIdx = groups.findIndex((g) => g.some((x) => x.id === over.id));
        if (activeGroupIdx < 0 || overGroupIdx < 0) {
            return;
        }
        const activeGroup = groups[activeGroupIdx];
        const activeIsPrimary = activeGroup[0].id === active.id;

        let reordered;
        if (activeIsPrimary) {
            // Move the whole group (primary + its subitems) at group granularity.
            // Dropping anywhere inside another group snaps to that group's
            // boundary, so a primary can never land between another primary and
            // its subitems (e.g. 10 cannot go between 9 and 9.1).
            if (activeGroupIdx === overGroupIdx) {
                return;
            }
            const newGroups = groups.filter((_, i) => i !== activeGroupIdx);
            let target = newGroups.findIndex((g) => g === groups[overGroupIdx]);
            // Land after the target group when moving downward, before when up.
            if (overGroupIdx > activeGroupIdx) {
                target += 1;
            }
            newGroups.splice(target, 0, activeGroup);
            reordered = newGroups.flat();
        } else {
            // Dragging a subitem: reorder only within its own parent group.
            // Drops outside the group are clamped to the group's boundaries.
            const parentGroup = activeGroup;
            const parentIds = parentGroup.map((x) => x.id);
            const subFrom = parentGroup.findIndex((x) => x.id === active.id);

            let subTo;
            if (parentIds.includes(over.id)) {
                subTo = parentGroup.findIndex((x) => x.id === over.id);
            } else {
                // Over target is outside this group: clamp to nearest end,
                // but never before the primary (index 0).
                const overIndex = data.findIndex((x) => x.id === over.id);
                const primaryIndex = data.findIndex((x) => x.id === parentGroup[0].id);
                subTo = overIndex < primaryIndex ? 1 : parentGroup.length - 1;
            }
            if (subTo < 1) {
                subTo = 1; // keep subitems after the primary
            }
            if (subTo === subFrom) {
                return;
            }
            const newParent = arrayMove([...parentGroup], subFrom, subTo);
            const newGroups = groups.map((g, i) => (i === activeGroupIdx ? newParent : g));
            reordered = newGroups.flat();
        }

        if (props.onChange) {
            props.onChange(applyNumbering(reordered, startNumberOf(data)));
        }
    }

    const handleChangeLanguage = (lang) => {
        setLanguage(lang);
    }
    async function handleAdd()  {
        const newitem = await getNewItemAsync();
        let newdata = [...props.data];
        // Seed the new item's tag from the last numbered item so it participates
        // in the sequence, then force a renumber to auto-number it.
        const lastTagged = [...props.data].reverse().find(x => x.tagEn && /\d/.test(x.tagEn));
        if (autoNumber && lastTagged) {
            newitem.tagEn = lastTagged.tagEn;
            newitem.tagCy = lastTagged.tagEn;
        }
        newdata.push(newitem);
        if (props.onChange) {
            props.onChange(applyNumbering(newdata, startNumberOf(props.data), true));
        }
    }
    const handleOnChange = (item) => {
        let newdata = [];
        for (var o of props.data) {
            if (o.id === item.id) {
                newdata.push(item);
            } else {
                newdata.push(o);
            }
        }
        if (props.onChange) {
            props.onChange(newdata);
        }
    }
    // Called when a tag edit is committed (input blur). Re-sequence the list so
    // an edited tag cascades to the rest, without renumbering on every keystroke.
    const handleTagCommit = () => {
        if (props.onChange) {
            props.onChange(applyNumbering([...props.data]));
        }
    }
    const handleMoveUp = (o) => {
        let newdata = [];
        for (var i = 0; i < props.data.length; i++) {
            if (i + 1 < props.data.length && props.data[i + 1].id === o.id) {
                newdata.push(o);
                newdata.push(props.data[i]);
                i++;
            } else {
                newdata.push(props.data[i]);
            }
        }
        if (props.onChange) {
            props.onChange(applyNumbering(newdata, startNumberOf(props.data)));
        }
    }
    const handleMoveDown = (o) => {
        let newdata = [];
        for (var i = 0; i < props.data.length; i++) {
            if (props.data[i].id === o.id && i + 1 < props.data.length) {
                newdata.push(props.data[i + 1]);
                newdata.push(o);
                i++;
            } else {
                newdata.push(props.data[i]);
            }
        }
        if (props.onChange) {
            props.onChange(applyNumbering(newdata, startNumberOf(props.data)));
        }
        //console.log("pd,nd = ",props.data.length, ",",newdata.length);
    }
    async function handleInsert(o)  {
        const newitem = await getNewItemAsync();
        // Seed the new item's tag from the item it is inserted before so it
        // participates in the sequence, then force a renumber to auto-number it.
        if (autoNumber && o.tagEn && /\d/.test(o.tagEn)) {
            newitem.tagEn = o.tagEn;
            newitem.tagCy = o.tagEn;
        }
        let newdata = [];
        for (var i = 0; i < props.data.length; i++) {
            if (props.data[i].id === o.id) {
                newdata.push(newitem);
                newdata.push(o);
            } else {
                newdata.push(props.data[i]);
            }
        }
        if (props.onChange) {
            props.onChange(applyNumbering(newdata, startNumberOf(props.data), true));
        }
    }
    const handleRemove = (o) => {
        let newdata = [];
        for (var i = 0; i < props.data.length; i++) {
            if (props.data[i].id === o.id) {
                // skip
            } else {
                newdata.push(props.data[i]);
            }
        }
        if (props.onChange) {
            props.onChange(applyNumbering(newdata, startNumberOf(props.data)));
        }
    }
    const handleCloneAgenda = (o) => {
        if (props.onCloneAgendaAndAttendees) {
            props.onCloneAgendaAndAttendees(o);
        }
    }
    return (
        <>
            <ButtonGroup className="mb-2">
                <Button size="sm" variant="success" onClick={handleAdd}>
                    Add Agenda Item
                </Button>
            </ButtonGroup>
            &nbsp;
            <ButtonGroup className="mb-2">
                <Button
                    size="sm"
                    variant={autoNumber ? "primary" : "outline-secondary"}
                    onClick={() => setAutoNumber(v => !v)}
                    title="Toggle automatic renumbering of agenda items on reorder, insert and add"
                >
                    Auto-numbering: {autoNumber ? "On" : "Off"}
                </Button>
            </ButtonGroup>
            &nbsp;
            <ButtonGroup className="mb-2">
                <MeetingAgendaItemClonePopup committee={props.committee} onChange={handleCloneAgenda} />
            </ButtonGroup>
            &nbsp;
            <ButtonGroup className="mb-2">
                <ToggleButton
                    key={1}
                    id={`radio-1`}
                    type="radio"
                    name="radio"
                    value="en"
                    checked={language === "en"}
                    onClick={() => { handleChangeLanguage("en"); }}
                    size="sm"
                    variant="success"
                >
                    English
                </ToggleButton>
                <ToggleButton
                    key={2}
                    id={`radio-2`}
                    type="radio"
                    name="radio"
                    value="cy"
                    checked={language === "cy"}
                    onClick={() => { handleChangeLanguage("cy"); }}
                    size="sm"
                    variant="success"
                >
                    Cymraeg
                </ToggleButton>
            </ButtonGroup>
            {props.data ? (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={props.data.map((item) => item.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        {props.data.map((item, index) => (
                            <AgendaItemEditor
                                data={item}
                                key={item.id}
                                language={language}
                                onChange={handleOnChange}
                                onTagCommit={handleTagCommit}
                                onMoveUp={handleMoveUp}
                                onMoveDown={handleMoveDown}
                                onInsert={handleInsert}
                                onRemove={handleRemove}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
            ) : ''}
        </>);
}

export default AgendaEditor;